import React, { useRef, useEffect, useState } from 'react';
import { Card, Col, Collapse, Row } from 'react-bootstrap';
import * as d3 from 'd3';
import EntitySurpDefTable from './EntitySurpDefTable';

interface SurpDefEntity {
  entity_id: number;
  entity_name: string;
  fiscal_year: number;
  net_revenue: number;
  net_expense: number;
  surdef: number;
}

interface EntitySurpDefOverviewProps {
  data: SurpDefEntity[];
  selectedEntityName?: string;
}

const EntitySurpDefOverview: React.FC<EntitySurpDefOverviewProps> = ({ data, selectedEntityName }) => {
  const overviewChartRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tabulatorRef = useRef<any>(null);
  const [showHeaderInfo, setShowHeaderInfo] = useState<boolean>(false);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  const formatEntityString = (value: string) => value.replace(/_/g, ' ');

  // Updated - D3 Chart Rendering
  useEffect(() => {
    if (!data || !data.length) return;

    const entityName = selectedEntityName || data[0].entity_name;
    const width = containerRef.current?.clientWidth ?? 1200;
    const height = width * 0.6;
    const margin = { top: 10, right: 10, bottom: 80, left: 75 };
    const subgroups: Array<keyof SurpDefEntity> = ['net_revenue','net_expense','surdef'];
    const groups = data.map(d => d.fiscal_year);

    const minValue = d3.min(data, d => d3.min(subgroups, key => Number(d[key]))) ?? 0;
    const maxValue = d3.max(data, d => d3.max(subgroups, key => Number(d[key]))) ?? 0;

    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const x0 = d3.scaleBand<string>()
      .domain(groups.map(String))
      .range([0, innerWidth])
      .paddingInner(0.1);

    const x1 = d3.scaleBand<string>()
      .domain(subgroups.map(String))
      .range([0, x0.bandwidth()])
      .padding(0.05);

    const color = d3.scaleOrdinal<string, string>()
      .domain(subgroups.map(String))
      .range(['#2e8b8b','#ffc63a','#B0B7BC'])
      .unknown('#ccc');

    const y = d3.scaleLinear()
      .domain([minValue < 0 ? minValue : 0, maxValue])
      .nice()
      .range([innerHeight, 0]);

    const svg = d3.select(overviewChartRef.current);
    svg.selectAll("*").remove();
    svg
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", [0, 0, width, height])
      .attr("style", "max-width:100%; height:auto;");

    // Remove existing tooltips
    d3.selectAll("body > .tooltip").remove();

    // Tooltip
    const tooltip = d3.select("body")
      .append("div")
      .attr("class","tooltip")
      .style("opacity",0)
      .style("position","absolute")
      .style("pointer-events","none")
      .style("background-color","#fff")
      .style("border","1px solid #ccc")
      .style("padding", "12px") 
      .style("font-size","18px")
      .style("border-radius", "6px");

    const chartGroup = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const yAxis = d3.axisLeft(y)
      .ticks(6)
      .tickFormat(d3.format("$,~"))
      .tickSize(-innerWidth); 

    chartGroup.append("g")
      .call(yAxis)
      .selectAll("line")
      .attr("stroke", "gray")
      .attr("stroke-dasharray", "7 7");

    // X Axis
    chartGroup.append("g")
      .attr("transform", `translate(0,${innerHeight})`) 
      .call(d3.axisBottom(x0).tickSizeOuter(0))
      .selectAll("text")
      .attr("transform","rotate(-30)")
      .style("text-anchor","end");

    // Entity Name under X Axis
    svg.append("text")
      .attr("x", width / 2)
      .attr("y", height - margin.bottom + 60)
      .attr("text-anchor", "middle")
      .style("font-size", "14px")
      .style("font-weight", "bold")
      .text(entityName);

    chartGroup.append("g")
      .selectAll("g")
      .data(data)
      .join("g")
      .attr("transform", d => `translate(${x0(String(d.fiscal_year))},0)`)
      .selectAll("rect")
      .data(d => subgroups.map(key => ({ key: String(key), value: Number(d[key]) ?? 0, fiscal_year: d.fiscal_year })))
      .join("rect")
      .attr("x", d => x1(d.key)!)
      .attr("y", d => d.value >= 0 ? y(d.value) : y(0))
      .attr("width", x1.bandwidth())
      .attr("height", d => Math.abs(y(d.value) - y(0)))
      .attr("fill", d => d.key === 'surdef' && d.value < 0 ? "#FF0000" : color(d.key)!)
      .on("mouseover", (event, d) => {
        tooltip.transition().duration(100).style("opacity", 0.9);
        const categoryLabel = d.key === 'surdef'
          ? (d.value >= 0 ? 'Surplus' : 'Deficit')
          : formatEntityString(d.key);

        tooltip.html(`
          <b>Year: ${d.fiscal_year}</b><br>
          Category: <span class="badge bg-secondary">${categoryLabel}</span><br>
          Value: <span class="badge bg-light text-dark">${formatCurrency(d.value)}</span>
        `);
        chartGroup.selectAll("rect").transition().duration(100).style("opacity", 0.2);
        d3.select(event.currentTarget).transition().duration(100).style("opacity", 1);
      })
      .on("mousemove", (event) => {
        tooltip.style("left", (event.pageX + 10) + "px")
              .style("top", (event.pageY - 28) + "px");
      })
      .on("mouseleave", () => {
        tooltip.transition().duration(100).style("opacity", 0);
        chartGroup.selectAll("rect").transition().duration(100).style("opacity", 1);
      });

  }, [data, selectedEntityName]);

  // Added - D3 Chart Download Handler
  const handleD3ChartDownload = () => {
    if (!overviewChartRef.current) return;

    const currentEntityName = selectedEntityName || data[0]?.entity_name || "Entity";
    const formattedName = currentEntityName.toLowerCase().replaceAll(" ", "_");

    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(overviewChartRef.current);
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      if (context) {
        context.fillStyle = "white";
        context.fillRect(0, 0, canvas.width, canvas.height);

        context.drawImage(img, 0, 0);
      }
      URL.revokeObjectURL(url);

      const pngUrl = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = pngUrl;
      a.download = `${formattedName}_surplus_deficit.png`;
      a.click();
    };
    img.src = url;
  };

  return (
    <>
      <Row className="mb-2">
        <Col xs={12}>
          <Card bg="secondary" text="white" style={{ border: "none", borderRadius: "0" }}>
            <Card.Body style={{ justifyItems: "center" }}>
              <div className="tu_tabHeader">
                <div className="tu-tabHeader__titleRow">
                  <h1 className="tu-tabHeader__title">
                    Surplus/Deficit</h1>
                    <button
                      type="button"
                      className="tu-tabHeader__infoBtn"
                      onClick={() => setShowHeaderInfo(!showHeaderInfo)}
                      aria-label="Entity Surplus/Deficit info"
                    >
                      <i className="fas fa-info"/>
                    </button>
                </div>
              </div>

              <Collapse in={showHeaderInfo}>
                <div>
                  <p className="lead mt-2 text-center">
                    The Surplus/Deficit tab allows you to analyze an entity's spending deficits and surpluses.
                  </p>
                  <p className="lead mt-2 text-center">
                    The information is presented in a bar graph format and table format. You can sort the table by entity, fiscal year, or monetary amounts for revenues, expenses, and surplus/deficits. You can also download the data to save the information
                  </p>
                  <ul className="text-center p-0" style={{ listStylePosition: 'inside' }}>
                    <li>Surplus/(Deficit) - shows the yearly revenue, expenditure, as well as an indication of a surplus or deficit.</li>
                  </ul>
                </div>
              </Collapse>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {data.length > 0 ? (
        <Card className="mb-4">        
          <div id="chart-container" ref={containerRef} style={{ width: "100%", padding: "20px" }}>
          <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
              <div
                className="btn btn-secondary"
                onClick={handleD3ChartDownload}
                style={{ margin: 0 }}
              >
                Download
              </div>
          </div>

            <svg
              ref={overviewChartRef}
              width="100%"
              height={containerRef.current ? containerRef.current.clientWidth * 0.6 : 600}
              viewBox={`0 0 ${containerRef.current ? containerRef.current.clientWidth : 1200} ${containerRef.current ? containerRef.current.clientWidth * 0.6 : 600}`}
              preserveAspectRatio="xMidYMid meet"
            />
          </div>

          <Card.Body>
            <EntitySurpDefTable 
              ref={tabulatorRef} 
              data={data} 
              entityName={selectedEntityName || data[0]?.entity_name}
            />
          </Card.Body>
        </Card>
      ) : (
        <Card className="mb-4">
          <div className="p-5 text-center">
            <div className="alert alert-light shadow-sm">
              No data available
            </div>
          </div>
        </Card>
      )}
    </>
  );
};

export default EntitySurpDefOverview;